import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useAppContext } from "../Context/AppContext";
import { isLoggedIn } from "../auth/authStorage";
import { explorerOnboardingQueryKey } from "../auth/authSession";
import { getMyExplorerOnboarding } from "../api/explorerOnboardingApi";
import { hasExplorerOnboarding } from "../explorer/hasExplorerOnboarding";
import { hasPendingExplorerMembershipEventCombo } from "../explorer/explorerSession";

/**
 * Explorer users without full membership: show sidebar shell but lock nav behind membership.
 * Paid or cancelled members use the normal sidebar behaviour.
 */
export function useMembershipSidebarGate() {
  const { user, objectId } = useAppContext();
  const location = useLocation();
  const authed = isLoggedIn();

  const { data: explorerMe } = useQuery({
    queryKey: explorerOnboardingQueryKey(objectId),
    queryFn: getMyExplorerOnboarding,
    enabled: authed && !!objectId,
    retry: false,
    staleTime: 60_000,
  });

  const isExplorer = useMemo(() => hasExplorerOnboarding(explorerMe), [explorerMe]);

  const isCancelled = useMemo(() => {
    if (user?.user?.status === "InActive") return true;
    const st = user?.user?.stripeStatus?.toLowerCase?.();
    return st === "canceled" || st === "cancelled";
  }, [user?.user?.status, user?.user?.stripeStatus]);

  const hasPaid = user?.user?.hasPaid === 1;

  /** Explorer + membership combo checkout: full registration UI, not explorer locked sidebar. */
  const isComboMembershipRegistration = useMemo(
    () =>
      location.pathname === "/register-user" &&
      hasPendingExplorerMembershipEventCombo(),
    [location.pathname],
  );

  const lockSidebarForMembership = useMemo(
    () =>
      authed &&
      !!user?.user &&
      !hasPaid &&
      !isCancelled &&
      isExplorer &&
      !isComboMembershipRegistration,
    [
      authed,
      user?.user,
      hasPaid,
      isCancelled,
      isExplorer,
      isComboMembershipRegistration,
    ],
  );

  const treatAsSidebarMember = hasPaid || isCancelled || lockSidebarForMembership;

  return { lockSidebarForMembership, treatAsSidebarMember };
}
